import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Mail } from "lucide-react";

const EMAIL_HREF =
  "mailto:hello@pixelkare.com?subject=AI%20App%20Development%20Enquiry&body=Hi%20Pixelkare%2C%20I%27d%20like%20to%20discuss%20building%20an%20AI-powered%20app.";

const PROJECTS = [
  {
    slug: "kokodelight",
    name: "KOKO Delight",
    url: "https://kokodelight.in",
    tagline: "Taste a home cook's special.",
    category: "Hyperlocal Food · Marketplace",
    year: "2025",
    timeline: "1 week",
    stack: ["React", "FastAPI", "MongoDB", "UPI Pay"],
    description:
      "A neighbourhood pre-booking platform that connects passionate home cooks with hungry neighbours — no restaurants, no dark kitchens. Each cook lists up to 10 portions a day, so every plate stays personal. Diners pick their city and area, browse the day's menu, and pre-book takeaway or home delivery. Cooks get paid directly on UPI.",
    highlights: [
      "City & area-based discovery with admin-curated neighbourhoods",
      "Per-cook portion cap (max 10/day) to protect quality",
      "Onboarding flow for new cooks to list dishes & set delivery options",
      "Built end-to-end in a single week using our AI pipelines",
    ],
  },
];

export default function Work() {
  return (
    <div data-testid="work-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, rgba(31,135,188,0.10), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-20">
          <p className="overline reveal" data-testid="work-eyebrow">
            <span className="inline-block h-1.5 w-1.5 bg-[#1f87bc] mr-3 align-middle" />
            Recent Work
          </p>
          <h1
            data-testid="work-heading"
            className="font-display reveal text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl text-[#0a0a0a]"
            style={{ animationDelay: "100ms" }}
          >
            Shipped with{" "}
            <span className="text-[#1f87bc]">AI</span>.
          </h1>
          <p
            className="reveal mt-10 text-lg md:text-xl text-[#52525b] max-w-2xl leading-relaxed"
            style={{ animationDelay: "200ms" }}
          >
            Real, live products — designed, built and launched by our
            AI-augmented pipeline. More case studies dropping soon.
          </p>
        </div>
      </section>

      {/* PROJECT LIST */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28"
        data-testid="work-list"
      >
        <div className="space-y-px bg-black/10">
          {PROJECTS.map((p) => (
            <article
              key={p.slug}
              data-testid={`project-${p.slug}`}
              className="bg-white p-8 md:p-12 hover-lift hover:bg-[#fcfbf7]"
            >
              <div className="grid grid-cols-12 gap-6 md:gap-10">
                <div className="col-span-12 md:col-span-7">
                  <p className="font-mono text-xs tracking-[0.2em] text-[#1f87bc] uppercase">
                    {p.category}
                  </p>
                  <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a0a0a] leading-[1]">
                    {p.name}
                  </h2>
                  <p className="mt-4 text-xl text-[#52525b] italic">
                    {p.tagline}
                  </p>
                  <p className="mt-6 text-[#52525b] leading-relaxed max-w-2xl">
                    {p.description}
                  </p>

                  <ul className="mt-8 space-y-2.5">
                    {p.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex gap-3 text-[#0a0a0a] text-sm md:text-base leading-relaxed"
                      >
                        <span className="text-[#1f87bc] mt-1.5">→</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`project-${p.slug}-link`}
                    className="mt-10 inline-flex items-center gap-3 bg-[#1f87bc] hover:bg-[#176d99] text-white px-6 py-3 transition-colors font-medium"
                  >
                    Visit {p.url.replace("https://", "")}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                <div className="col-span-12 md:col-span-5 md:border-l md:border-black/10 md:pl-10">
                  <div className="grid grid-cols-2 gap-y-8">
                    <div>
                      <p className="overline">Year</p>
                      <p className="mt-2 font-display text-2xl text-[#0a0a0a]">
                        {p.year}
                      </p>
                    </div>
                    <div>
                      <p className="overline">Timeline</p>
                      <p className="mt-2 font-display text-2xl text-[#0a0a0a]">
                        {p.timeline}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="overline">Stack</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.stack.map((s) => (
                          <span
                            key={s}
                            className="font-mono text-xs px-3 py-1.5 border border-black/15 text-[#0a0a0a]"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}

          {/* Coming soon placeholder */}
          <div
            data-testid="project-coming-soon"
            className="bg-[#f3f1ec] p-12 md:p-16 flex flex-col items-start"
          >
            <p className="overline">Up next</p>
            <h3 className="font-display text-3xl md:text-4xl mt-4 text-[#0a0a0a] leading-[1.05]">
              More case studies, <br />
              <span className="text-[#a1a1aa]">shipping every week.</span>
            </h3>
            <p className="mt-5 text-[#52525b] max-w-xl">
              Want to be the next one featured here? Send a brief — we'll
              quote, scope and ship inside seven days.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-[#f3f1ec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-display text-4xl md:text-6xl text-[#0a0a0a] leading-[1]">
                Have an idea? <br />
                <span className="text-[#1f87bc]">Let's build the next one.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 flex flex-col md:items-end gap-4">
              <a
                href={EMAIL_HREF}
                data-testid="work-cta-email"
                className="inline-flex items-center gap-3 bg-[#1f87bc] hover:bg-[#176d99] text-white px-7 py-4 font-medium transition-colors"
              >
                <Mail className="h-5 w-5" />
                Email us
              </a>
              <Link
                to="/about"
                data-testid="work-cta-about"
                className="inline-flex items-center gap-3 text-[#52525b] hover:text-[#0a0a0a] transition-colors font-mono uppercase text-xs tracking-[0.2em]"
              >
                Why AI changes everything
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
